// src/components/VendorFoldersTab.jsx
// Admin tab for managing vendor SharePoint folder structure (with nesting)

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, X, Loader2, Folder, FolderOpen, Edit2, Trash2, Check, 
  ChevronDown, ChevronRight, Eye, EyeOff,
  FolderPlus, ArrowUp, ArrowDown
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// ============================================================================
// HOOK: useVendorFolderTemplates
// ============================================================================
export function useVendorFolderTemplates(organizationId) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFolders = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('vendor_folder_templates')
        .select('*')
        .eq('organization_id', organizationId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setFolders(data || []);
    } catch (err) {
      console.error('Error fetching vendor folder templates:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const addFolder = async (folderData) => {
    const { data, error } = await supabase
      .from('vendor_folder_templates')
      .insert({ organization_id: organizationId, ...folderData })
      .select()
      .single();
    if (error) throw error;
    setFolders(prev => [...prev, data].sort((a, b) => a.sort_order - b.sort_order));
    return data;
  };

  const updateFolder = async (id, updates) => {
    const { data, error } = await supabase
      .from('vendor_folder_templates')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    setFolders(prev => prev.map(f => f.id === id ? data : f).sort((a, b) => a.sort_order - b.sort_order));
    return data;
  };

  const deleteFolder = async (id) => {
    const { error } = await supabase
      .from('vendor_folder_templates')
      .delete()
      .eq('id', id);
    if (error) throw error;
    setFolders(prev => prev.filter(f => f.id !== id && f.parent_id !== id));
  };

  const folderTree = buildTree(folders);

  return { folders, folderTree, loading, addFolder, updateFolder, deleteFolder, refresh: fetchFolders };
}

// Build nested tree from flat list
function buildTree(items) {
  const map = {};
  const roots = [];

  items.forEach(item => { map[item.id] = { ...item, children: [] }; });

  items.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.id]);
    } else if (!item.parent_id) {
      roots.push(map[item.id]);
    }
  });

  const sortChildren = (nodes) => {
    nodes.sort((a, b) => a.sort_order - b.sort_order);
    nodes.forEach(n => sortChildren(n.children));
  };
  sortChildren(roots);
  return roots;
}

// Get flat list of active folder paths for SharePoint creation
export function getVendorFolderPaths(folders) {
  const activeFolders = folders.filter(f => f.is_active);
  const paths = [];

  const buildPath = (folder, parentPath) => {
    const currentPath = parentPath ? parentPath + '/' + folder.display_name : folder.display_name;
    paths.push(currentPath);
    const children = activeFolders.filter(f => f.parent_id === folder.id);
    children.sort((a, b) => a.sort_order - b.sort_order);
    children.forEach(child => buildPath(child, currentPath));
  };

  const roots = activeFolders.filter(f => !f.parent_id);
  roots.sort((a, b) => a.sort_order - b.sort_order);
  roots.forEach(root => buildPath(root, ''));

  return paths;
}

// Default vendor folders to seed
const DEFAULT_VENDOR_FOLDERS = [
  { display_name: 'Contracts', sort_order: 1 },
  { display_name: 'Invoices', sort_order: 2 },
  { display_name: 'Correspondence', sort_order: 3 },
  { display_name: 'Insurance & Certificates', sort_order: 4 },
];


// ============================================================================
// COMPONENT: VendorFoldersTab
// ============================================================================
export default function VendorFoldersTab({ organizationId, primaryColor, secondaryColor, showToast }) {
  const {
    folders, folderTree, loading,
    addFolder, updateFolder, deleteFolder
  } = useVendorFolderTemplates(organizationId);

  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: '', description: '' });
  const [addingTo, setAddingTo] = useState(null);
  const [addForm, setAddForm] = useState({ display_name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Auto-expand top-level on load
  useEffect(() => {
    if (folderTree.length > 0 && Object.keys(expanded).length === 0) {
      const exp = {};
      folderTree.forEach(f => { exp[f.id] = true; });
      setExpanded(exp);
    }
  }, [folderTree]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getNextSortOrder = (parentId) => {
    const siblings = folders.filter(f =>
      parentId ? f.parent_id === parentId : !f.parent_id
    );
    if (siblings.length === 0) return 1;
    return Math.max(...siblings.map(s => s.sort_order)) + 1;
  };

  const handleAdd = async () => {
    if (!addForm.display_name.trim()) return;
    setSaving(true);
    try {
      await addFolder({
        parent_id: addingTo === '__root__' ? null : addingTo,
        display_name: addForm.display_name.trim(),
        description: addForm.description.trim() || null,
        sort_order: getNextSortOrder(addingTo === '__root__' ? null : addingTo),
      });
      setAddForm({ display_name: '', description: '' });
      setAddingTo(null);
      showToast(addingTo === '__root__' ? 'Folder added' : 'Subfolder added');
    } catch (err) {
      if (err.message?.includes('duplicate') || err.message?.includes('unique')) {
        showToast('A folder with that name already exists at this level', 'error');
      } else {
        showToast(err.message, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (folder) => {
    setEditingId(folder.id);
    setEditForm({ display_name: folder.display_name, description: folder.description || '' });
  };

  const handleSaveEdit = async () => {
    if (!editForm.display_name.trim()) return;
    setSaving(true);
    try {
      await updateFolder(editingId, {
        display_name: editForm.display_name.trim(),
        description: editForm.description.trim() || null,
      });
      setEditingId(null);
      showToast('Folder updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (folder) => {
    const childCount = folders.filter(f => f.parent_id === folder.id).length;
    const msg = childCount > 0
      ? 'Delete "' + folder.display_name + '" and its ' + childCount + ' subfolder(s)?'
      : 'Delete "' + folder.display_name + '"?';
    if (!confirm(msg)) return;
    try {
      await deleteFolder(folder.id);
      showToast('Folder deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleToggleActive = async (folder) => {
    try {
      await updateFolder(folder.id, { is_active: !folder.is_active });
      showToast(folder.is_active ? 'Folder hidden' : 'Folder restored');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleMove = async (folder, direction) => {
    const siblings = folders
      .filter(f => folder.parent_id ? f.parent_id === folder.parent_id : !f.parent_id)
      .sort((a, b) => a.sort_order - b.sort_order);
    const idx = siblings.findIndex(s => s.id === folder.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const other = siblings[swapIdx];
    try {
      await updateFolder(folder.id, { sort_order: other.sort_order });
      await updateFolder(other.id, { sort_order: folder.sort_order });
      showToast('Order updated');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleSeedDefaults = async () => {
    if (folders.length > 0 && !confirm('This will add default folders. Existing folders will not be affected. Continue?')) return;
    setSeeding(true);
    try {
      const existingNames = folders.filter(f => !f.parent_id).map(f => f.display_name.toLowerCase());
      const nextOrder = folders.length > 0 ? Math.max(...folders.filter(f => !f.parent_id).map(f => f.sort_order)) + 1 : 1;
      let added = 0;
      for (let i = 0; i < DEFAULT_VENDOR_FOLDERS.length; i++) {
        const def = DEFAULT_VENDOR_FOLDERS[i];
        if (!existingNames.includes(def.display_name.toLowerCase())) {
          await addFolder({
            parent_id: null,
            display_name: def.display_name,
            sort_order: nextOrder + i,
          });
          added++;
        }
      }
      showToast(added > 0 ? added + ' default folder(s) added' : 'All defaults already exist');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSeeding(false);
    }
  };

  // Render a single folder row
  const renderFolder = (folder, depth) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expanded[folder.id];
    const isEditing = editingId === folder.id;
    const isAddingChild = addingTo === folder.id;

    return (
      <div key={folder.id}>
        <div
          className={'flex items-center gap-2 py-2 px-3 border-b hover:bg-gray-50 transition-colors group ' + (!folder.is_active ? 'opacity-50' : '')}
          style={{ paddingLeft: (12 + depth * 24) + 'px' }}
        >
          {/* Expand/collapse */}
          <button
            onClick={() => toggleExpand(folder.id)}
            className="w-5 h-5 flex items-center justify-center flex-shrink-0"
            style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          >
            {isExpanded
              ? <ChevronDown className="w-4 h-4" style={{ color: secondaryColor }} />
              : <ChevronRight className="w-4 h-4" style={{ color: secondaryColor }} />
            }
          </button>

          {/* Folder icon */}
          {isExpanded && hasChildren
            ? <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
            : <Folder className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
          }

          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <input
                value={editForm.display_name}
                onChange={e => setEditForm(p => ({ ...p, display_name: e.target.value }))}
                className="border rounded px-2 py-1 text-sm flex-1"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
              <button onClick={handleSaveEdit} disabled={saving} className="p-1 hover:bg-green-50 rounded text-green-600">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button onClick={() => setEditingId(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <span className="flex-1 text-sm font-medium">{folder.display_name}</span>

              {!folder.is_active && (
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Hidden</span>
              )}

              {folder.description && (
                <span className="text-xs hidden lg:inline" style={{ color: secondaryColor }}>
                  {folder.description}
                </span>
              )}

              {/* Actions (hover) */}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleMove(folder, 'up')} className="p-1 hover:bg-gray-100 rounded" title="Move up">
                  <ArrowUp className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                </button>
                <button onClick={() => handleMove(folder, 'down')} className="p-1 hover:bg-gray-100 rounded" title="Move down">
                  <ArrowDown className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                </button>
                <button
                  onClick={() => {
                    setAddingTo(folder.id);
                    setAddForm({ display_name: '', description: '' });
                    setExpanded(prev => ({ ...prev, [folder.id]: true }));
                  }}
                  className="p-1 hover:bg-blue-50 rounded"
                  title="Add subfolder"
                  style={{ color: primaryColor }}
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleStartEdit(folder)} className="p-1 hover:bg-gray-100 rounded" title="Edit">
                  <Edit2 className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                </button>
                <button onClick={() => handleToggleActive(folder)} className="p-1 hover:bg-gray-100 rounded" title={folder.is_active ? 'Hide' : 'Show'}>
                  {folder.is_active
                    ? <EyeOff className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                    : <Eye className="w-3.5 h-3.5" style={{ color: secondaryColor }} />
                  }
                </button>
                <button onClick={() => handleDelete(folder)} className="p-1 hover:bg-red-50 rounded text-red-500" title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Inline add subfolder form */}
        {isAddingChild && (
          <div
            className="flex items-center gap-2 py-2 px-3 bg-blue-50 border-b"
            style={{ paddingLeft: (12 + (depth + 1) * 24) + 'px' }}
          >
            <FolderPlus className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
            <input
              value={addForm.display_name}
              onChange={e => setAddForm(p => ({ ...p, display_name: e.target.value }))}
              placeholder="Subfolder name..."
              className="border rounded px-2 py-1 text-sm flex-1"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleAdd();
                if (e.key === 'Escape') setAddingTo(null);
              }}
            />
            <input
              value={addForm.description}
              onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Description (optional)"
              className="border rounded px-2 py-1 text-sm flex-1 hidden md:block"
            />
            <button onClick={handleAdd} disabled={saving || !addForm.display_name.trim()} className="p-1 hover:bg-green-50 rounded text-green-600 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button onClick={() => setAddingTo(null)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Children */}
        {isExpanded && hasChildren && folder.children.map(child => renderFolder(child, depth + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
        <span style={{ color: secondaryColor }}>Loading vendor folders...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="font-semibold text-lg" style={{ color: primaryColor }}>Vendor Folder Structure</h2>
          <p className="text-sm mt-1" style={{ color: secondaryColor }}>
            Define the subfolder structure created in SharePoint when setting up a new vendor.
            Hover over a folder to reorder, add subfolders, rename, hide, or delete.
          </p>
        </div>
        {folderTree.length === 0 && (
          <button
            onClick={handleSeedDefaults}
            disabled={seeding}
            className="px-3 py-1.5 text-white rounded text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
            style={{ backgroundColor: primaryColor }}
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
            Load Defaults
          </button>
        )}
      </div>

      {/* Folder Tree */}
      <div className="border rounded-lg overflow-hidden">
        {folderTree.length === 0 ? (
          <div className="text-center py-8" style={{ color: secondaryColor }}>
            <Folder className="w-10 h-10 mx-auto mb-2" />
            <p>No vendor folders defined yet.</p>
            <p className="text-xs mt-1">Click "Load Defaults" above to start with the standard structure, or add folders manually below.</p>
          </div>
        ) : (
          folderTree.map(f => renderFolder(f, 0))
        )}
      </div>

      {/* Add top-level folder */}
      {addingTo === '__root__' ? (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <h3 className="font-medium text-sm mb-3" style={{ color: primaryColor }}>Add Top-Level Folder</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs mb-1 font-medium">Folder Name *</label>
              <input
                value={addForm.display_name}
                onChange={e => setAddForm(p => ({ ...p, display_name: e.target.value }))}
                placeholder="e.g., Proposals"
                className="w-full border rounded px-3 py-2 text-sm"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              />
            </div>
            <div>
              <label className="block text-xs mb-1 font-medium">Description (optional)</label>
              <input
                value={addForm.description}
                onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                placeholder="What goes in this folder?"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setAddingTo(null); setAddForm({ display_name: '', description: '' }); }} className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={saving || !addForm.display_name.trim()}
              className="px-3 py-1.5 text-white rounded text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
              style={{ backgroundColor: primaryColor }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Folder
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => { setAddingTo('__root__'); setAddForm({ display_name: '', description: '' }); }}
          className="flex items-center gap-2 text-sm px-3 py-2 border border-dashed rounded-lg hover:bg-gray-50 transition-colors w-full justify-center"
          style={{ color: primaryColor, borderColor: primaryColor }}
        >
          <Plus className="w-4 h-4" /> Add Top-Level Folder
        </button>
      )}

      {/* Tip */}
      <div className="text-xs pt-2 border-t" style={{ color: secondaryColor }}>
        <strong>Tip:</strong> These folders are created in SharePoint under <code>Vendors/[Vendor Name]/</code> when you click "Create SharePoint Folder" on a vendor.
        Hover over any folder to see actions.
      </div>
    </div>
  );
}
